import { Button, LinearProgress } from '@material-ui/core';
import { Attachment } from '@material-ui/icons';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import ErrorIcon from '@mui/icons-material/Error';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Avatar, Step, StepConnector, StepLabel, Stepper } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import { bytesToSize, camelize } from '../../utils/commonUtils';
import './Csv-Uploader.scss';

const baseStyle = {
  width: '35%',
  height: 150,
  marginLeft: '33%',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderWidth: 1,
  borderRadius: 10,
  borderColor: '#CCCCCC',
  borderStyle: 'dashed',
  backgroundColor: '#fff',
  color: '#CCCCCC',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  minHeight: 120,
};

const activeStyle = {
  borderColor: '#03E67C',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

const CsvUploader = (props) => {
  const {
    onClose,
    pageTitle,
    downloadLink,
    csvUploadStatus,
    onFileReadingComplete,
    validFormatError,
    setValidFormatError,
    dataExistError,
    setDataAlreadyExistError,
    sectionTItle,
    listName,
    csvUploadErrorMsg,
    setDataExistError,
    validationErrors,
    setValidationErrors,
  } = props;

  const [tabValue, setTabValue] = useState(0);
  const [stepperActiveStep, setStepperActiveStep] = useState(0);
  const [uploadError, setUploadError] = useState(false);
  const [progress, setProgress] = useState(0);
  const [acceptedFile, setAcceptedFile] = useState(null);
  const logo = '/assets/img/hudini-logo-black.png';

  useEffect(() => {
    if (tabValue === 0) setStepperActiveStep(0);
    if (tabValue === 1 || tabValue === 2 || tabValue === 3) setStepperActiveStep(1);
    if (tabValue === 4) setStepperActiveStep(2);
  }, [tabValue]);

  const handleTabChange = (newValue) => {
    setTabValue(newValue);
  };

  const onDropAccepted = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      handleDroppedFile(acceptedFiles[0]);
    }
  }, []);

  const onDropRejected = useCallback(() => {
    setUploadError(true);
  }, []);

  const onDocDelete = () => {
    setAcceptedFile(null);
    setUploadError(false);
    setValidFormatError(false);
    if (setValidationErrors) setValidationErrors([]);
  };

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open } =
    useDropzone({
      accept: '.csv,.xlsx,.xls,text/csv,application/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      multiple: false,
      noClick: true,
      onDropAccepted,
      onDropRejected,
    });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
      ...(isDragActive ? activeStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept],
  );

  useEffect(() => {
    switch (csvUploadStatus) {
      case 'uploading':
        setTabValue(2);
        break;
      case 'error':
        setTabValue(3);
        break;
      case 'success':
        setTabValue(4);
        break;
      case 'server-error':
        setTabValue(5);
        break;
      default:
    }
  }, [csvUploadStatus]);

  useEffect(() => {
    if (dataExistError) {
      setTabValue(3);
    }
  }, [dataExistError]);

  const sanitizeCellValue = (val) => {
    if (val == null) return '';
    return String(val)
      .replace(/\uFEFF/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/[\u200B-\u200D\u2060\uFFF9-\uFFFB]/g, '')
      .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
      .replace(/[\u2018\u2019\u201A]/g, "'")
      .replace(/[\u201C\u201D\u201E]/g, '"')
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/\u2026/g, '...')
      .replace(/\r\n/g, ' ')
      .replace(/[\r\n]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const parseCSVText = (text) => {
    const rows = [];
    let current = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      const next = text[i + 1];
      if (inQuotes) {
        if (ch === '"') {
          if (next === '"') { field += '"'; i++; } else { inQuotes = false; }
        } else { field += ch; }
      } else {
        if (ch === '"') { inQuotes = true; }
        else if (ch === ',') { current.push(field); field = ''; }
        else if (ch === '\r' && next === '\n') { current.push(field); field = ''; rows.push(current); current = []; i++; }
        else if (ch === '\n' || ch === '\r') { current.push(field); field = ''; rows.push(current); current = []; }
        else { field += ch; }
      }
    }
    current.push(field);
    if (current.some((f) => f.trim() !== '')) rows.push(current);
    return rows.filter((r) => r.some((f) => f.trim() !== ''));
  };

  const readFileData = async (fileObj) => {
    setProgress(50);
    try {
      let rows;
      try {
        const data = await fileObj.arrayBuffer();
        const book = XLSX.read(new Uint8Array(data), {
          type: 'array',
          cellStyles: false,
          cellFormula: false,
          cellHTML: false,
          cellDates: false,
        });
        const wsname = book.SheetNames[0];
        const ws = book.Sheets[wsname];
        const ref = ws['!ref'];
        if (ref) {
          const match = ref.match(/:([A-Z]+)(\d+)$/);
          if (match) {
            const lastRow = parseInt(match[2], 10);
            const MAX_ROWS = 5000;
            if (lastRow > MAX_ROWS) {
              const colPart = ref.replace(/:.*/, ':') + match[1] + MAX_ROWS;
              ws['!ref'] = colPart;
            }
          }
        }
        rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', blankrows: true });
      } catch (excelErr) {
        const text = await fileObj.text();
        rows = parseCSVText(text);
      }
      if (!Array.isArray(rows) || rows.length < 2) {
        setUploadError(true);
        return false;
      }

      const cleanHeader = (h) => {
        return sanitizeCellValue(String(h || ''))
          .replace(/\(.*?\)/g, '')
          .replace(/[^a-zA-Z0-9\s]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      };

      let headerRowIdx = 0;
      const firstRowNonEmpty = rows[0].filter((c) => String(c || '').trim() !== '').length;
      if (rows.length > 2 && firstRowNonEmpty <= 1) {
        headerRowIdx = 1;
      }

      const headersRaw = rows[headerRowIdx] || [];
      const headers = headersRaw.map((h) => camelize(cleanHeader(h)));
      const dataRows = rows.slice(headerRowIdx + 1);
      const objects = dataRows.map((r, i) => {
        const obj = { __rowNum: headerRowIdx + 2 + i };
        headers.forEach((h, j) => {
          if (h) obj[h] = r[j] == null ? '' : sanitizeCellValue(String(r[j]));
        });
        return obj;
      });
      const cleanRows = objects?.filter((row) =>
        Object?.entries(row)?.some(([k, v]) => k !== '__rowNum' && String(v ?? '')?.trim() !== ''),
      );
      if (cleanRows.length === 0) {
        setUploadError(true);
        return false;
      }
      setProgress(100);
      onFileReadingComplete(cleanRows);
    } catch (e) {
      setUploadError(true);
      return false;
    }
  };

  const handleDroppedFile = async (fileObj) => {
    setProgress(0);
    setAcceptedFile(fileObj);
    await readFileData(fileObj);
  };

  const onCloseModal = (e) => {
    setAcceptedFile(null);
    setUploadError(false);
    setValidFormatError(false);
    setTabValue(0);
    onClose(e);
  };
  return (
    <div className='d-flex csv-upload-container  flex-column justify-content-between'>
      <div className='row col-md-12'>
        <div className='csv-upload-container-header mb-4 d-flex'>Upload {pageTitle}</div>
        <div className='header-logo'>
          <img src={logo} alt='logo' />
        </div>
        <div className='csv-upload-container-stepper'>
          <Stepper
            activeStep={stepperActiveStep}
            alternativeLabel
            connector={
              <StepConnector
                style={{
                  marginLeft: '-2.9%',
                  marginRight: '-2.9%',
                }}
              />
            }
          >
            <Step key='Template'>
              <StepLabel icon={tabValue === 0 ? <CircleOutlinedIcon /> : <CheckCircleIcon />}>
                Template
                {tabValue === 0 && (
                  <div className='stepper-top-arrow'>
                    <KeyboardArrowDownIcon />
                  </div>
                )}
              </StepLabel>
            </Step>
            <Step key='Upload'>
              <StepLabel
                icon={
                  tabValue === 4 ? (
                    <CheckCircleIcon />
                  ) : tabValue === 0 || tabValue === 1 ? (
                    <CircleOutlinedIcon />
                  ) : (
                    <Avatar className='error-icon-avatar'>
                      <ErrorIcon className='error-icon' />
                    </Avatar>
                  )
                }
              >
                Upload
                {tabValue > 0 && tabValue < 4 && (
                  <div className='stepper-top-arrow'>
                    <KeyboardArrowDownIcon />
                  </div>
                )}
              </StepLabel>
            </Step>
          </Stepper>
        </div>
        {tabValue === 0 && (
          <div className='row col-md-12 mb-4 mt-4'>
            <div className='csv-upload-container-title my-4'>Download Excel Template</div>
            <div className='csv-upload-container-content'>
              <p>
                To minimize error while mapping, we have designed a template ,
                <br />
                Which ensures easy mapping of all the columns in the excel sheet to the required
                form fields.
                <br />
              </p>
              <p className='mt-2'>Download below the Excel Template for {pageTitle} form page</p>
            </div>
            <div className='download-button'>
              <a
                className='download-button-white'
                href={downloadLink}
                download='Channel Info Template.xlsx'
              >
                <Button variant='contained' size='large' color='primary'>
                  <FileDownloadOutlinedIcon className='mx-2' />
                  Download Template
                </Button>
              </a>
            </div>
            <div className='skip-button'>
              <p className='col-md-12'>
                Already have the template?
                <a className='pointer mx-3' onClick={(e) => handleTabChange(1)}>
                  Skip This
                </a>
              </p>
            </div>
          </div>
        )}
        {tabValue === 1 && (
          <div className='row col-md-12 mb-4 mt-2'>
            <div className='csv-upload-container-title my-4'>Upload your file</div>
            <div className='csv-upload-container-content'>
              <p>
                Upload the filled excel template for {pageTitle}. <br />
                Upload only in .csv, .xls or .xlsx formats
              </p>
            </div>
            {acceptedFile ? (
              <div
                className={
                  'col-md-12 p-2 uploading-status ' +
                  (uploadError || validFormatError ? 'error' : '')
                }
              >
                <div
                  className={
                    'd-flex justify-content-between align-items-center py-2 accepted-container ' +
                    (uploadError || validFormatError ? 'upload-error' : '')
                  }
                >
                  <div> {acceptedFile.name} </div>
                  <div>{acceptedFile.size && bytesToSize(acceptedFile.size)}</div>
                  {uploadError || validFormatError ? (
                    <div className='d-flex align-items-center'>
                      <div>Upload Failed</div>
                      <div>
                        <CancelIcon
                          className='pointer me-1'
                          onClick={(e) => onDocDelete()}
                        ></CancelIcon>
                      </div>
                    </div>
                  ) : (
                    <div className='upload-progress d-flex align-items-center'>
                      <div className='upload-progress-title'>Uploading</div>
                      <div className='upload-progress-bar'>
                        <LinearProgress variant='determinate' color='secondary' value={progress} />
                      </div>
                      <CancelIcon
                        className='pointer me-1'
                        onClick={(e) => onDocDelete()}
                      ></CancelIcon>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className='row pt-2'>
                <div className='img-drop-zone'>
                  <div {...getRootProps({ style })}>
                    <input {...getInputProps()} />
                    {!isDragActive && (
                      <div className='d-flex align-items-center'>
                        <Attachment />
                        <span className='ms-1'>
                          Drag and drop or{' '}
                          <span className='text-highlight pointer' onClick={open}>
                            Browse
                          </span>
                        </span>
                      </div>
                    )}
                    {isDragActive && (
                      <div className='align-items-center'>
                        <div className='mb-2 text-center'>
                          <img
                            className='icon'
                            src='/assets/icons/service-request/upload-rounded.svg'
                            alt='icon'
                          />
                        </div>
                        <span>Drop your files here to upload</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            {uploadError && (
              <div className='mt-4 csv-upload-container-error'>
                There was some error while uploading the file.{' '}
                <span onClick={(e) => onDocDelete()} className='link-text text-red'>
                  Try Again
                </span>
              </div>
            )}
            {validFormatError && (
              <>
                {!dataExistError && (
                  <>
                    <div className='mt-4 csv-upload-container-error'>
                      There was some error while uploading the file.{' '}
                    </div>
                    <div className='mt-4 csv-upload-container-error'>
                      Please check if the data in the document satisfy the acceptance criteria and{' '}
                      <span onClick={(e) => onDocDelete()} className='link-text text-red'>
                        Try Again
                      </span>
                    </div>
                  </>
                )}
                {dataExistError && (
                  <div className='mt-4 csv-upload-container-error'>
                    All rows in the file already exist.{' '}
                    <span onClick={(e) => onDocDelete()} className='link-text text-red'>
                      Try Again
                    </span>
                  </div>
                )}
                {validationErrors && validationErrors.length > 0 && (
                  <div className='mt-3' style={{ width: '80%', margin: '0 auto', maxHeight: '130px', overflowY: 'auto', textAlign: 'left', fontSize: '13px', background: '#fff5f5', border: '1px solid #e8bcbc', borderRadius: '6px', padding: '12px 16px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '6px', color: '#c0392b' }}>Issues found:</div>
                    <ul style={{ margin: 0, paddingLeft: '18px', listStyleType: 'disc' }}>
                      {validationErrors.map((err, idx) => (
                        <li key={idx} style={{ marginBottom: '3px', color: '#555' }}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {tabValue === 2 && (
          <div className='row col-md-12 mb-4 mt-2'>
            <div className='row col-md-12 upload-success'>
              <div className='loading-icon'>
                <div id='circularG'>
                  <div id='circularG_1' className='circularG'></div>
                  <div id='circularG_2' className='circularG'></div>
                  <div id='circularG_3' className='circularG'></div>
                  <div id='circularG_4' className='circularG'></div>
                  <div id='circularG_5' className='circularG'></div>
                  <div id='circularG_6' className='circularG'></div>
                  <div id='circularG_7' className='circularG'></div>
                  <div id='circularG_8' className='circularG'></div>
                </div>
              </div>
              <div className='csv-upload-container-title my-4'>Uploading</div>
              <div className='csv-upload-container-content'>
                <p>File name: {acceptedFile?.name}</p>
              </div>
            </div>
          </div>
        )}
        {tabValue === 3 && (
          <div className='row col-md-12 mb-4 mt-2'>
            <div className='row col-md-12 mb-4 mt-2'>
              <div className='csv-upload-container-title my-4'>Unable to Upload document!</div>
              <div className='csv-upload-container-content'>
                <p>
                  The information related to {pageTitle?.toLowerCase()} in your excel file is
                  already included and mapped. Kindly review your file and re-upload it with
                  accurate details.
                </p>
              </div>
              <div className='col-md-12 pt-4 d-flex justify-content-center mt-auto mb-4'>
                <div>
                  <Button
                    variant='outlined'
                    size='large'
                    className='px-5 py-2'
                    color='secondary'
                    onClick={(e) => {
                      onDocDelete();
                      setTabValue(1);
                      setDataExistError(false);
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    variant='contained'
                    size='large'
                    color='primary'
                    className='mx-4 py-2'
                    onClick={(e) => {
                      onDocDelete();
                      setTabValue(1);
                      setDataExistError(false);
                    }}
                  >
                    Re-Upload
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        {tabValue === 4 && (
          <div className='row col-md-12 mb-4 mt-2'>
            <div className='row col-md-12 upload-success'>
              <div className='success-icon'>
                <CheckCircleIcon
                  style={{
                    fontSize: '65px',
                  }}
                />
              </div>
              <div className='csv-upload-container-title my-4'>Uploaded</div>
              <div className='csv-upload-container-content'>
                <p>File name: {acceptedFile?.name}</p>
              </div>
              <div className='ok-button'>
                <Button
                  variant='contained'
                  size='large'
                  color='primary'
                  className='px-5 py-2'
                  onClick={(e) => onCloseModal(e)}
                >
                  Ok
                </Button>
              </div>
              <div className='return-telephony mt-2'>
                <p>
                  Return to {sectionTItle} - {listName}
                </p>
              </div>
            </div>
          </div>
        )}
        {tabValue === 5 && (
          <div className='row col-md-12 mb-4 mt-2'>
            <div className='row col-md-12 mb-4 mt-2'>
              <div className='csv-upload-container-title my-4'>Unable to Upload document!</div>
              <div className='csv-upload-container-content'>
                {csvUploadErrorMsg ? (
                  <p>{csvUploadErrorMsg}</p>
                ) : (
                  <p>
                    The information related to {pageTitle} in your Excel file is already included
                    and mapped. Kindly review your file and re-upload it with accurate details
                  </p>
                )}
              </div>
              <div className='col-md-12 pt-4 d-flex justify-content-center mt-auto mb-4'>
                <div>
                  <Button
                    variant='outlined'
                    size='large'
                    className='px-5 py-2'
                    color='secondary'
                    onClick={(e) => onCloseModal(e)}
                  >
                    OK
                  </Button>
                </div>
              </div>
              <div className='return-telephony mt-2  d-flex justify-content-center'>
                <p>
                  Return to {sectionTItle} - {listName}
                </p>
              </div>
            </div>
          </div>
        )}
        {/* {tabValue === 2 && (
          <div className="row col-md-12 mb-4 mt-2">
            <div>
              <div className="csv-upload-container-title my-4">
                Replace extension for the following Rooms?
              </div>
              <div className="csv-upload-container-content">
                <p>
                  These Rooms already have extensions mapped to them on Elevate
                  - Telephony.
                  <br />
                  Do you want to replace the extensions for these rooms? <br />
                  On selecting option No, the extensions for these rooms will
                  not be uploaded to elevate.
                </p>
              </div>
              <div className="duplicate-extension">
                <div className="extension-overflow">
                  {conflictList?.map((extension) => (
                    <div
                      className="p-3 mt-1 duplicate-extension-list d-flex justify-content-between"
                      key={conflictList.roomNumber}
                    >
                      <div key={extension.roomNumber}>
                        {extension?.roomNumber}
                      </div>
                      <div> List: {extension?.extensionNumber}</div>
                    </div>
                  ))}
                </div>
                <div className="col-md-12 pt-4 d-flex justify-content-center mt-auto mb-4">
                  <div>
                    <Button
                      variant="outlined"
                      size="large"
                      className="px-5 py-2"
                      color="secondary"
                      onClick={(e) => setTabValue(5)}
                    >
                      No
                    </Button>
                    <Button
                      variant="contained"
                      size="large"
                      color="primary"
                      className="mx-4 py-2"
                      onClick={(e) => onCsvDataSubmit(true, conflictList)}
                    >
                      Replace
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )} */}
      </div>
      <div className='d-flex justify-content-between mt-auto mb-4'>
        <div>
          {tabValue === 1 && (
            <Button
              variant='outlined'
              size='large'
              color='secondary'
              onClick={(e) => {
                onDocDelete();
                handleTabChange(tabValue - 1);
              }}
            >
              Back
            </Button>
          )}
          {(tabValue === 0 || tabValue === 1) && (
            <Button
              variant='outlined'
              size='large'
              color='secondary'
              className='mx-4'
              onClick={(e) => onCloseModal(e)}
            >
              Cancel
            </Button>
          )}
        </div>
        <div>
          {tabValue === 0 && (
            <Button
              variant='contained'
              size='large'
              color='primary'
              onClick={(e) => handleTabChange(tabValue + 1)}
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CsvUploader;
